import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/CountryWhatsAppInput.css';
import {
  DEFAULT_WHATSAPP_COUNTRY,
  getCountryIso2FromPhone,
  normalizeWhatsAppValue,
  validateWhatsAppNumberByCountry,
} from '../utils/whatsappPhone';

const CountryWhatsAppInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  onValidationChange,
  defaultCountry = DEFAULT_WHATSAPP_COUNTRY,
  placeholder = 'Enter WhatsApp number',
  autoFocus = false,
  disabled = false,
  theme = 'light', // 'light' | 'dark' | 'portal' (portal follows :root data-theme)
  inputClassName = '',
  containerClassName = '',
  buttonClassName = '',
}) => {
  const parsedCountry = useMemo(() => getCountryIso2FromPhone(value), [value]);
  const [selectedCountry, setSelectedCountry] = useState(parsedCountry || defaultCountry);

  useEffect(() => {
    if (parsedCountry && parsedCountry !== selectedCountry) {
      setSelectedCountry(parsedCountry);
    }
  }, [parsedCountry, selectedCountry]);

  const numericValue = useMemo(() => String(value || '').replace(/\D/g, ''), [value]);

  const emitValidation = (phoneE164, countryIso2) => {
    const isValid = validateWhatsAppNumberByCountry(phoneE164, countryIso2);
    onValidationChange?.({
      isValid,
      countryIso2,
    });
    return isValid;
  };

  const themeClass =
    theme === 'portal'
      ? 'country-whatsapp-input--portal'
      : theme === 'dark'
        ? 'country-whatsapp-input--dark'
        : '';

  const fieldRootRef = useRef(null);

  /** Dropdown lives inside the narrow flag column — pin it to the viewport so width/stacking stay correct. */
  useLayoutEffect(() => {
    const root = fieldRootRef.current;
    if (!root) return undefined;

    const resetList = (list) => {
      if (!list) return;
      list.style.position = '';
      list.style.top = '';
      list.style.left = '';
      list.style.right = '';
      list.style.width = '';
      list.style.minWidth = '';
      list.style.maxWidth = '';
      list.style.zIndex = '';
      list.style.margin = '';
    };

    const repositionOpenDropdown = () => {
      const openBtn = root.querySelector('.flag-dropdown.open');
      const lists = root.querySelectorAll('.country-list');
      lists.forEach((list) => {
        if (!openBtn || list.classList.contains('hide')) {
          resetList(list);
          return;
        }
        const rect = openBtn.getBoundingClientRect();
        const width = Math.min(300, window.innerWidth - 16);
        list.style.position = 'fixed';
        list.style.top = `${rect.bottom + 6}px`;
        list.style.left = `${Math.max(8, rect.left)}px`;
        list.style.width = `${width}px`;
        list.style.minWidth = `${width}px`;
        list.style.maxWidth = `${width}px`;
        list.style.zIndex = '10050';
        list.style.margin = '0';
      });
    };

    const observer = new MutationObserver(repositionOpenDropdown);
    observer.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    });
    window.addEventListener('resize', repositionOpenDropdown);
    window.addEventListener('scroll', repositionOpenDropdown, true);
    repositionOpenDropdown();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', repositionOpenDropdown);
      window.removeEventListener('scroll', repositionOpenDropdown, true);
      root.querySelectorAll('.country-list').forEach(resetList);
    };
  }, []);

  return (
    <div ref={fieldRootRef} className="country-whatsapp-field">
    <PhoneInput
      country={selectedCountry || defaultCountry}
      value={numericValue}
      onChange={(rawValue, countryData) => {
        const countryIso2 = countryData?.countryCode || selectedCountry || defaultCountry;
        const phoneE164 = normalizeWhatsAppValue(rawValue);
        setSelectedCountry(countryIso2);
        onChange?.(phoneE164, {
          countryIso2,
          dialCode: countryData?.dialCode || null,
          isValid: emitValidation(phoneE164, countryIso2),
        });
      }}
      onBlur={() => {
        const normalized = normalizeWhatsAppValue(value);
        onBlur?.(normalized, {
          countryIso2: selectedCountry,
          isValid: emitValidation(normalized, selectedCountry),
        });
      }}
      countryCodeEditable={false}
      enableSearch
      searchPlaceholder="Search country"
      autocompleteSearch
      disableSearchIcon
      placeholder={placeholder}
      inputProps={{
        id,
        name,
        required: false,
        autoFocus,
        disabled,
      }}
      containerClass={`country-whatsapp-input${themeClass ? ` ${themeClass}` : ''} ${containerClassName}`.trim()}
      inputClass={`country-whatsapp-input__field ${inputClassName}`.trim()}
      buttonClass={`country-whatsapp-input__button ${buttonClassName}`.trim()}
      dropdownClass="country-whatsapp-input__dropdown"
      searchClass="country-whatsapp-input__search"
      dropdownStyle={{
        position: 'fixed',
        zIndex: 10050,
        width: 300,
        minWidth: 300,
        marginTop: 0,
        marginBottom: 0,
      }}
    />
    </div>
  );
};

export default CountryWhatsAppInput;
